import DreamSDK from '@dream/sdk-web';
import { DREAMCRM_API_KEY, DREAMCRM_BASE_URL } from '@/constants';

const DEBUG = process.env.NODE_ENV === 'development';

const log = (...args: unknown[]): void => {
  if (!DEBUG) return;
  console.info('%c[dreamcrm]', 'color:#a855f7;font-weight:bold', ...args);
};

export type ProductViewEvent = {
  key: 'product_view';
  params: { product_id: string };
};

export type CategoryViewEvent = {
  key: 'category_view';
  params: { category_id: string };
};

export type SetCartEvent = {
  key: 'set_cart';
  params: { product_id: string; quantity: number; price: number };
};

export type RemoveFromCartEvent = {
  key: 'remove_from_cart';
  params: { product_id: string };
};

export type AddToFavouritesEvent = {
  key: 'add_to_favourites';
  params: {
    product_id: string;
    productName?: string;
    productUrl?: string;
    productPictureUrl?: string;
    productBrand?: string;
    productPrice?: string;
    productOldPrice?: string;
  };
};

export type RemoveFromFavouritesEvent = {
  key: 'remove_from_favourites';
  params: { product_id: string };
};

export type DreamCRMEvent =
  | ProductViewEvent
  | CategoryViewEvent
  | SetCartEvent
  | RemoveFromCartEvent
  | AddToFavouritesEvent
  | RemoveFromFavouritesEvent;

export const initDreamSDK = (): void => {
  if (typeof window === 'undefined') return;

  log('initialize', { baseUrl: DREAMCRM_BASE_URL, debug: DEBUG });

  DreamSDK.initialize({
    apiKey: DREAMCRM_API_KEY as string,
    baseUrl: DREAMCRM_BASE_URL,
    debug: DEBUG,
  });
};

// Ключ, под которым SDK хранит websiteId авторизованного устройства
const SDK_WEBSITE_ID_KEY = 'dream_sdk_website_id';

// websiteId, для которого запрос assign уже отправлен, но ещё не завершён
let pendingWebsiteId: string | null = null;

const getStoredWebsiteId = (): string | null => {
  try {
    return localStorage.getItem(SDK_WEBSITE_ID_KEY);
  } catch {
    return null;
  }
};

const saveWebsiteId = (id: string) => {
  localStorage.setItem(SDK_WEBSITE_ID_KEY, id);
};

const clearWebsiteId = () => {
  localStorage.removeItem(SDK_WEBSITE_ID_KEY);
};

export const authorizeDreamSDK = (
  websiteId: string | number | null | undefined,
): void => {
  if (!websiteId || typeof window === 'undefined') return;

  const id = String(websiteId);

  if (id === getStoredWebsiteId() || id === pendingWebsiteId) {
    log('authorize skipped (already authorized)', { websiteId: id });
    return;
  }

  pendingWebsiteId = id;
  log('authorize', { websiteId: id });
  DreamSDK.authorize(id);
  saveWebsiteId(id);
};

export const logoutDreamSDK = (): void => {
  pendingWebsiteId = null;
  log('logout');
  DreamSDK.logout();
  clearWebsiteId();
};

export const sendDreamEvent = (event: DreamCRMEvent): void => {
  log('sendEvent', event);
  DreamSDK.sendEvent(event);
};
